import { useEffect, useRef, useState } from 'react';
import { Modal } from './ui';
import { STAGES } from './StatusPill.jsx';

// Phase 4 — captures the terminal DISPOSITION (how an offer / rejected /
// withdrawn application actually resolved) plus a free-text closeReason. Mirrors
// DismissReasonModal: presets are quick-pick chips, the reason is verbatim.
// Used two ways from ApplicationsPage:
//   mode="move" — shown when dragging a card INTO rejected/withdrawn; doubles as
//                 the close-out confirm (replaces the old plain ConfirmDialog).
//   mode="edit" — opened from the drawer's Outcome section to set/edit later
//                 (the only way to record offer_accepted vs offer_declined,
//                 which happens after a card is already in the Offer column).
//
// DISPOSITIONS is the single frontend source of truth, mirroring the backend
// services/dispositions.js list (the route enforces the same values).

export const DISPOSITIONS = {
  offer: [
    { value: 'offer_accepted', label: 'Accepted' },
    { value: 'offer_declined', label: 'I declined' },
    { value: 'offer_expired', label: 'Expired / rescinded' },
  ],
  rejected: [
    { value: 'rejected_after_apply', label: 'After applying (no screen)' },
    { value: 'rejected_after_screen', label: 'After screening' },
    { value: 'rejected_after_interview', label: 'After interview' },
    { value: 'rejected_after_offer', label: 'After offer' },
    { value: 'rejected_no_reason', label: 'No reason given' },
  ],
  withdrawn: [
    { value: 'withdrawn_other_offer', label: 'Took another offer' },
    { value: 'withdrawn_lost_interest', label: 'Lost interest' },
    { value: 'withdrawn_other', label: 'Other' },
  ],
};

export const statusHasDisposition = (status) => Object.prototype.hasOwnProperty.call(DISPOSITIONS, status);

export function dispositionLabel(value) {
  if (!value) return null;
  for (const list of Object.values(DISPOSITIONS)) {
    const found = list.find((d) => d.value === value);
    if (found) return found.label;
  }
  return null;
}

const statusLabelOf = (status) => STAGES[status]?.label ?? status;

export default function DispositionModal({ status, jobTitle, current, mode = 'move', onConfirm, onCancel }) {
  const presets = DISPOSITIONS[status] || [];
  const [disposition, setDisposition] = useState(current?.disposition ?? null);
  const [closedReason, setClosedReason] = useState(current?.closedReason ?? '');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);
  const label = statusLabelOf(status);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disposition, closedReason]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await onConfirm(disposition ?? null, closedReason.trim());
    } finally {
      setSubmitting(false);
    }
  };

  const header = mode === 'edit'
    ? `${label} — outcome`
    : jobTitle ? `Move "${jobTitle}" to ${label}?` : `Move to ${label}?`;
  const confirmText = submitting
    ? 'saving…'
    : mode === 'edit'
      ? 'Save outcome'
      : disposition || closedReason.trim()
        ? `Move to ${label}`
        : `Close as ${label}`;

  return (
    <Modal size="sm" labelledById="disposition-title" initialFocusRef={inputRef} onClose={onCancel}>
      <header className="border-b border-slate-800 p-4">
        <p className="text-[10px] uppercase tracking-wide text-brand-500">Outcome</p>
        <h2 id="disposition-title" className="mt-0.5 truncate text-base font-semibold text-slate-100">
          {header}
        </h2>
        {mode === 'move' && (
          <p className="mt-1 text-xs text-amber-300">
            This closes the application out of your active pipeline. You can reopen it later by moving it back.
          </p>
        )}
        <p className="mt-0.5 text-xs text-slate-500">
          Optional — records HOW it resolved so your analytics can tell a won offer from a declined one,
          and split rejections by stage. Leave blank to skip.
        </p>
      </header>

      <div className="space-y-3 p-4">
        <div className="flex flex-wrap gap-1.5">
          {presets.map((p) => {
            const selected = disposition === p.value;
            return (
              <button
                key={p.value}
                type="button"
                onClick={() => setDisposition(selected ? null : p.value)}
                className={
                  'rounded-full border px-2.5 py-0.5 text-xs transition ' +
                  (selected
                    ? 'border-brand-500 bg-brand-600/20 text-brand-400'
                    : 'border-slate-700 text-slate-300 hover:border-brand-600 hover:text-brand-400')
                }
              >
                {p.label}
              </button>
            );
          })}
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wide text-slate-400">Notes (optional)</label>
          <textarea
            ref={inputRef}
            value={closedReason}
            onChange={(e) => setClosedReason(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder='e.g. "Went with a more senior candidate" / "Comp below range"'
            className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950/40 px-3 py-2 text-sm text-slate-100 focus:border-brand-500 focus:outline-none"
          />
          <p className="mt-1 text-xs text-slate-500">{closedReason.length}/500 · ⌘/Ctrl+Enter to confirm</p>
        </div>
      </div>

      <footer className="flex items-center justify-end gap-2 border-t border-slate-800 bg-slate-900/60 p-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-400 hover:border-slate-500 hover:text-slate-200 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-brand-500 disabled:opacity-50"
        >
          {confirmText}
        </button>
      </footer>
    </Modal>
  );
}
